import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getOrganization, deleteOrganization, getUsers, addMember, removeMember, createUser, updateOrganization, getMemberNotes, createNote, deleteNote, updateUserPermissions } from '../../api';
import Modal from '../../components/Modal';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import {
  ArrowLeftIcon,
  TrashIcon,
  UserPlusIcon,
  PlusIcon,
  XMarkIcon,
  PhotoIcon,
  UserGroupIcon,
  PencilSquareIcon,
  CalendarIcon,
  TicketIcon,
  ChatBubbleLeftEllipsisIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  SwatchIcon,
  FolderIcon,
  ChatBubbleOvalLeftEllipsisIcon
} from '@heroicons/react/24/outline';

export default function OrganizationDetailPage() {
  const MAX_LOGO_SIZE_MB = 10;
  const MAX_LOGO_SIZE_BYTES = MAX_LOGO_SIZE_MB * 1024 * 1024;

  const { orgId } = useParams();
  const navigate = useNavigate();
  const { user, refreshOrgs } = useAuth();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showCreateMember, setShowCreateMember] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [memberForm, setMemberForm] = useState({ username: '', password: '', displayName: '', role: 'member' });
  const [editForm, setEditForm] = useState({ name: '', description: '', logo: '' });
  const [showNotes, setShowNotes] = useState(null);
  const [memberNotes, setMemberNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [showPermissions, setShowPermissions] = useState(null);
  const [permForm, setPermForm] = useState({});

  const isOwner = org?.ownerId === user?._id || user?.role === 'admin' || (user?.role === 'leader' && org?.members?.includes(user?._id));
  const canManageBoards = ['owner', 'leader', 'admin'].includes(user?.role) || !!user?.permissions?.manageBoards;

  useEffect(() => {
    loadData();
  }, [orgId]);

  const loadData = async () => {
    try {
      const [orgRes, usersRes] = await Promise.all([
        getOrganization(orgId),
        getUsers()
      ]);
      setOrg(orgRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this organization? This cannot be undone.')) return;
    try {
      await deleteOrganization(orgId);
      await refreshOrgs();
      navigate('/organizations');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleAddExistingMember = async (userId) => {
    try {
      const res = await addMember(orgId, userId);
      setOrg(res.data);
      await refreshOrgs();
      setSuccess('Member added!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      const res = await removeMember(orgId, userId);
      setOrg(res.data);
      await refreshOrgs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await createUser({ ...memberForm });
      await addMember(orgId, res.data._id);
      await loadData();
      await refreshOrgs();
      setShowCreateMember(false);
      setMemberForm({ username: '', password: '', displayName: '', role: 'member' });
      setSuccess('Member created and added!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create member');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await updateOrganization(orgId, editForm);
      setOrg(res.data);
      await refreshOrgs();
      setShowEdit(false);
      setSuccess('Organization updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setError(`Logo is too large. Maximum file size is ${MAX_LOGO_SIZE_MB} MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setEditForm(prev => ({ ...prev, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const openEdit = () => {
    setEditForm({ name: org.name, description: org.description || '', logo: org.logo || '' });
    setShowEdit(true);
  };

  const availableMembers = users.filter(u =>
    (u.role === 'member' || u.role === 'leader') && !org?.members?.includes(u._id) && u._id !== org?.ownerId
  );

  const openNotes = async (memberId) => {
    setShowNotes(memberId);
    setNewNote('');
    try {
      const res = await getMemberNotes(memberId);
      setMemberNotes(res.data);
    } catch (err) {
      setMemberNotes([]);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !showNotes) return;
    try {
      await createNote({ memberId: showNotes, organizationId: orgId, content: newNote });
      setNewNote('');
      const res = await getMemberNotes(showNotes);
      setMemberNotes(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deleteNote(noteId);
      setMemberNotes(prev => prev.filter(n => n._id !== noteId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete note');
    }
  };

  const openPermissions = (memberId) => {
    const member = users.find(u => u._id === memberId);
    setPermForm(member?.permissions || {});
    setShowPermissions(memberId);
  };

  const handleSavePermissions = async () => {
    if (!showPermissions) return;
    try {
      await updateUserPermissions(showPermissions, permForm);
      await loadData();
      setShowPermissions(null);
      setSuccess('Permissions updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update permissions');
    }
  };

  if (loading) return <Loading />;
  if (!org) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Organization not found</div>;

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate('/organizations')}>
            <ArrowLeftIcon className="icon" />
          </button>
          <div>
            <h2>{org.name}</h2>
            <div className="subtitle">Organization Details</div>
          </div>
        </div>
        {isOwner && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={openEdit}>
              <PencilSquareIcon className="icon" /> Edit
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              <TrashIcon className="icon" /> Delete
            </button>
          </div>
        )}
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Org Header */}
      <div className="org-detail-header">
        <div className="org-detail-logo">
          {org.logo ? <img src={org.logo} alt="" /> : org.name?.charAt(0)?.toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: 20, fontWeight: 700 }}>{org.name}</h3>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Owner: {org.ownerName}</div>
          {org.description && (
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 8 }}>{org.description}</div>
          )}
        </div>
        <div style={{ textAlign: 'center', padding: '0 16px' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent)' }}>{org.members?.length || 0}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Members</div>
        </div>
      </div>

      {/* Quick Shortcuts */}
      <div className="org-shortcuts">
        <NavLink to={`/org/${orgId}/calendar`} className="shortcut-card">
          <CalendarIcon className="shortcut-icon" />
          <span>Calendar</span>
        </NavLink>
        <NavLink to={`/org/${orgId}/events`} className="shortcut-card">
          <TicketIcon className="shortcut-icon" />
          <span>Events</span>
        </NavLink>
        <NavLink to={`/org/${orgId}/feedback`} className="shortcut-card">
          <ChatBubbleLeftEllipsisIcon className="shortcut-icon" />
          <span>Feedback</span>
        </NavLink>
        {canManageBoards && (
          <NavLink to={`/org/${orgId}/boards`} className="shortcut-card">
            <ClipboardDocumentListIcon className="shortcut-icon" />
            <span>Boards</span>
          </NavLink>
        )}
        {user?.role === 'admin' && (
          <>
            <NavLink to={`/org/${orgId}/outfit`} className="shortcut-card">
              <SwatchIcon className="shortcut-icon" />
              <span>Outfit</span>
            </NavLink>
            <NavLink to={`/org/${orgId}/documents`} className="shortcut-card">
              <FolderIcon className="shortcut-icon" />
              <span>Documents</span>
            </NavLink>
            <NavLink to={`/org/${orgId}/discord`} className="shortcut-card">
              <ChatBubbleOvalLeftEllipsisIcon className="shortcut-icon" />
              <span>Discord</span>
            </NavLink>
          </>
        )}
      </div>

      {/* Members Section */}
      <div className="card">
        <div className="card-header">
          <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserGroupIcon className="icon" /> Members
          </span>
          {isOwner && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowAddMember(!showAddMember); setShowCreateMember(false); }}>
                <UserPlusIcon className="icon" /> Add Existing
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => { setShowCreateMember(!showCreateMember); setShowAddMember(false); }}>
                <PlusIcon className="icon" /> Create New
              </button>
            </div>
          )}
        </div>

        {showAddMember && (
          <div style={{ marginBottom: 16, padding: 16, background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
            <h4 style={{ fontSize: 14, marginBottom: 8 }}>Available Members</h4>
            {availableMembers.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No available members to add</div>
            ) : (
              <div className="members-list">
                {availableMembers.map(u => (
                  <div key={u._id} className="member-item">
                    <div className="member-info">
                      <div className="member-avatar avatar-circle">
                        {u.avatar ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : u.displayName?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div className="member-name">{u.displayName}</div>
                        <div className="member-role">@{u.username}</div>
                      </div>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={() => handleAddExistingMember(u._id)}>Add</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {showCreateMember && (
          <div style={{ marginBottom: 16, padding: 16, background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
            <h4 style={{ fontSize: 14, marginBottom: 12 }}>Create New Member</h4>
            <form onSubmit={handleCreateMember}>
              <div className="form-group">
                <label>Username</label>
                <input className="form-input" value={memberForm.username} onChange={e => setMemberForm({...memberForm, username: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Display Name</label>
                <input className="form-input" value={memberForm.displayName} onChange={e => setMemberForm({...memberForm, displayName: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input className="form-input" type="password" value={memberForm.password} onChange={e => setMemberForm({...memberForm, password: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select className="form-input" value={memberForm.role} onChange={e => setMemberForm({...memberForm, role: e.target.value})}>
                  <option value="member">Member</option>
                  <option value="leader">Leader</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary btn-sm">Create & Add</button>
            </form>
          </div>
        )}

        <div className="members-list">
          {/* Owner always shown at top */}
          {(() => {
            const ownerUser = users.find(u => u._id === org.ownerId)
              || (user._id === org.ownerId ? user : null)
              || { _id: org.ownerId, displayName: org.ownerName || 'Owner', username: '', avatar: '', role: 'owner' };
            return (
              <div key={org.ownerId} className="member-item">
                <div className="member-info">
                  <div className="member-avatar avatar-circle" style={{ background: 'var(--accent)' }}>
                    {ownerUser.avatar ? <img src={ownerUser.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ownerUser.displayName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div className="member-name">{ownerUser.displayName}</div>
                    {ownerUser.username && <div className="member-role">@{ownerUser.username}</div>}
                  </div>
                </div>
                <span className="badge badge-purple" style={{ fontSize: 11 }}>Owner</span>
              </div>
            );
          })()}
          {(!org.members || org.members.length === 0) && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>No members yet</div>
          )}
          {org.members?.map(memberId => {
            const member = users.find(u => u._id === memberId);
            if (!member) return null;
            return (
              <div key={memberId} className="member-item">
                <div className="member-info">
                  <div className="member-avatar avatar-circle" style={{ background: member.role === 'leader' ? 'var(--warning)' : 'var(--success)' }}>
                    {member.avatar ? <img src={member.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : member.displayName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <div className="member-name">{member.displayName}</div>
                    <div className="member-role">@{member.username}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className={`badge ${member.role === 'leader' ? 'badge-warning' : 'badge-success'}`} style={{ fontSize: 11 }}>{member.role}</span>
                  {isOwner && (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={() => openPermissions(memberId)} title="Permissions" style={{ color: 'var(--accent)' }}>
                        <ShieldCheckIcon className="icon" />
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => openNotes(memberId)} title="Notes" style={{ color: 'var(--info)' }}>
                        <DocumentTextIcon className="icon" />
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleRemoveMember(memberId)} style={{ color: 'var(--danger)' }}>
                        <XMarkIcon className="icon" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Organization Modal */}
      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} title="Edit Organization"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleEdit}>Save Changes</button>
          </>
        }
      >
        <form onSubmit={handleEdit}>
          <div className="form-group">
            <label>Organization Name</label>
            <input className="form-input" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-input" value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={3} />
          </div>
          <div className="form-group">
            <label>Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {editForm.logo ? (
                <div className="org-logo" style={{ width: 56, height: 56 }}>
                  <img src={editForm.logo} alt="logo" />
                </div>
              ) : (
                <div className="org-logo" style={{ width: 56, height: 56 }}><PhotoIcon className="icon" /></div>
              )}
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                Upload Image
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
              </label>
              {editForm.logo && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditForm({...editForm, logo: ''})} style={{ color: 'var(--danger)' }}>
                  Remove
                </button>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              Maximum file size: <strong>{MAX_LOGO_SIZE_MB} MB</strong>.
            </div>
          </div>
        </form>
      </Modal>

      {/* Notes Modal */}
      <Modal isOpen={!!showNotes} onClose={() => setShowNotes(null)} title={`Notes: ${users.find(u => u._id === showNotes)?.displayName || ''}`}
        footer={<button className="btn btn-secondary" onClick={() => setShowNotes(null)}>Close</button>}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            className="form-input"
            placeholder="Add a note..."
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddNote()}
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary btn-sm" onClick={handleAddNote}>Add</button>
        </div>
        {memberNotes.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 16 }}>No notes yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {memberNotes.map(note => (
              <div key={note._id} style={{ padding: '10px 12px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 14 }}>{note.content}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {note.createdByName} • {new Date(note.createdAt).toLocaleString()}
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteNote(note._id)} style={{ color: 'var(--danger)', padding: 2 }}>
                    <TrashIcon style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Permissions Modal */}
      <Modal isOpen={!!showPermissions} onClose={() => setShowPermissions(null)} title={`Permissions: ${users.find(u => u._id === showPermissions)?.displayName || ''}`}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowPermissions(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSavePermissions}>Save Permissions</button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { key: 'manageBoards', label: 'Kanban Board Access', desc: 'Can view and edit kanban boards' },
            { key: 'manageMembers', label: 'Manage Members', desc: 'Can add new members and manage them in the organization' },
            { key: 'manageCalendar', label: 'Manage Calendar', desc: 'Can create and edit calendar events' },
            { key: 'manageEvents', label: 'Manage Events', desc: 'Can create, edit and delete events' },
            { key: 'manageNotes', label: 'Member Notes', desc: 'Can add and view notes about members' },
            { key: 'manageOutfit', label: 'Edit Outfit', desc: 'Can add and edit outfit items for rider and horse' },
            { key: 'manageDiscord', label: 'Manage Discord', desc: 'Can view and edit Discord integration settings' },
          ].map(perm => (
            <label key={perm.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '10px 12px', background: 'var(--bg-primary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <input
                type="checkbox"
                checked={!!permForm[perm.key]}
                onChange={e => setPermForm(prev => ({ ...prev, [perm.key]: e.target.checked }))}
                style={{ marginTop: 2 }}
              />
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{perm.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{perm.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </Modal>
    </div>
  );
}
